import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowLeft, MapPin, BedDouble, Bath, Users, Trash2, Save, RefreshCw, Link2, Plus, CheckCircle2, AlertCircle, Images } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import StateBadge from "@/components/StateBadge";
import {
    PROPERTY_STATE_LABELS,
    TASK_STATE_LABELS,
    TASK_TYPE_LABELS,
    INCIDENT_STATE_LABELS,
    INCIDENT_TYPE_LABELS,
    PRIORITY_LABELS,
} from "@/lib/dictionaries";
import { useAuth } from "@/contexts/AuthContext";

export default function PropertyDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { meta } = useAuth();
    const [data, setData] = useState(null);
    const [editing, setEditing] = useState(false);
    const [form, setForm] = useState(null);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    const [clients, setClients] = useState([]);

    useEffect(() => {
        api.get(`/properties/${id}`).then((r) => {
            setData(r.data);
            setForm(r.data.property);
        });
        api.get("/clients").then((r) => setClients(r.data || [])).catch(() => {});
    }, [id]);

    if (!data || !form) {
        return <div className="zentra-skeleton h-72" />;
    }

    const { property, tasks, incidents } = data;

    const save = async () => {
        setSaving(true);
        setError("");
        try {
            const { data: updated } = await api.patch(`/properties/${id}`, form);
            setData((d) => ({ ...d, property: updated }));
            setForm(updated);
            setEditing(false);
        } catch (err) {
            setError(formatApiError(err));
        } finally {
            setSaving(false);
        }
    };

    const remove = async () => {
        if (!window.confirm("¿Eliminar esta propiedad? Esta acción no se puede deshacer.")) return;
        await api.delete(`/properties/${id}`);
        navigate("/propiedades");
    };

    return (
        <div className="space-y-6" data-testid="property-detail">
            <Link to="/propiedades" className="inline-flex items-center gap-2 text-sm text-zentra-muted hover:text-zentra-ink">
                <ArrowLeft size={14} /> Volver a propiedades
            </Link>

            <div className="zentra-card-static p-6 zentra-accent-rule pl-7">
                <div className="flex flex-wrap items-start justify-between gap-4">
                    <div>
                        <p className="text-[11px] font-mono-zentra tracking-wider text-zentra-muted uppercase">
                            {property.code}
                        </p>
                        <h2 className="font-display text-2xl font-bold text-zentra-ink mt-1">{property.name}</h2>
                        <div className="flex items-center gap-2 text-sm text-zentra-muted mt-2">
                            <MapPin size={14} />
                            {property.address}
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <StateBadge value={property.state} dict={PROPERTY_STATE_LABELS} />
                        {!editing && (
                            <button className="zentra-btn-ghost" onClick={() => setEditing(true)} data-testid="edit-property">
                                Editar
                            </button>
                        )}
                        {editing && (
                            <button className="zentra-btn-primary" onClick={save} disabled={saving} data-testid="save-property-edit">
                                <Save size={14} /> {saving ? "Guardando…" : "Guardar"}
                            </button>
                        )}
                        <button className="zentra-btn-danger" onClick={remove} data-testid="delete-property">
                            <Trash2 size={14} />
                        </button>
                    </div>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-6">
                    <Spec icon={BedDouble} value={property.bedrooms} label="Habitaciones" />
                    <Spec icon={Bath} value={property.bathrooms} label="Baños" />
                    <Spec icon={Users} value={property.capacity} label="Capacidad" />
                    <div className="rounded-md border border-zentra-border bg-zentra-bg/30 p-3">
                        <p className="text-[10px] uppercase tracking-widest text-zentra-muted">Tipo</p>
                        <p className="font-display font-semibold text-zentra-ink mt-1 capitalize">{property.type}</p>
                    </div>
                </div>

                {editing && (
                    <div className="mt-5 grid md:grid-cols-2 gap-3">
                        <div>
                            <label className="zentra-label">Código</label>
                            <input
                                className="zentra-input font-mono-zentra"
                                value={form.code || ""}
                                onChange={(e) => setForm({ ...form, code: e.target.value })}
                                data-testid="edit-property-code"
                            />
                        </div>
                        <div>
                            <label className="zentra-label">Nombre</label>
                            <input
                                className="zentra-input"
                                value={form.name}
                                onChange={(e) => setForm({ ...form, name: e.target.value })}
                                data-testid="edit-property-name"
                            />
                        </div>
                        <div className="md:col-span-2">
                            <label className="zentra-label">Dirección</label>
                            <input
                                className="zentra-input"
                                value={form.address}
                                onChange={(e) => setForm({ ...form, address: e.target.value })}
                                placeholder="C/ Marina 12, 4ºA, Valencia"
                                data-testid="edit-property-address"
                            />
                        </div>
                        <div>
                            <label className="zentra-label">Tipo de inmueble</label>
                            <select
                                className="zentra-select"
                                value={form.type || "apartment"}
                                onChange={(e) => setForm({ ...form, type: e.target.value })}
                                data-testid="edit-property-type"
                            >
                                <option value="apartment">Apartamento</option>
                                <option value="loft">Loft</option>
                                <option value="studio">Estudio</option>
                                <option value="penthouse">Ático</option>
                                <option value="house">Casa</option>
                                <option value="villa">Villa</option>
                                <option value="room">Habitación</option>
                                <option value="other">Otro</option>
                            </select>
                        </div>
                        <div>
                            <label className="zentra-label">Estado</label>
                            <select
                                className="zentra-select"
                                value={form.state}
                                onChange={(e) => setForm({ ...form, state: e.target.value })}
                                data-testid="edit-property-state"
                            >
                                {(meta?.property_states || Object.keys(PROPERTY_STATE_LABELS)).map((s) => (
                                    <option key={s} value={s}>
                                        {PROPERTY_STATE_LABELS[s]?.label || s}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label className="zentra-label">Habitaciones</label>
                            <input
                                type="number"
                                min="0"
                                className="zentra-input"
                                value={form.bedrooms ?? 1}
                                onChange={(e) => setForm({ ...form, bedrooms: Number(e.target.value) })}
                                data-testid="edit-property-bedrooms"
                            />
                        </div>
                        <div>
                            <label className="zentra-label">Baños</label>
                            <input
                                type="number"
                                min="0"
                                className="zentra-input"
                                value={form.bathrooms ?? 1}
                                onChange={(e) => setForm({ ...form, bathrooms: Number(e.target.value) })}
                                data-testid="edit-property-bathrooms"
                            />
                        </div>
                        <div>
                            <label className="zentra-label">Capacidad (pax)</label>
                            <input
                                type="number"
                                min="1"
                                className="zentra-input"
                                value={form.capacity ?? 2}
                                onChange={(e) => setForm({ ...form, capacity: Number(e.target.value) })}
                                data-testid="edit-property-capacity"
                            />
                        </div>
                        <div>
                            <label className="zentra-label">Cliente</label>
                            <select
                                className="zentra-select"
                                value={form.client_id || ""}
                                onChange={(e) => setForm({ ...form, client_id: e.target.value || null })}
                                data-testid="edit-property-client"
                            >
                                <option value="">Sin asignar</option>
                                {clients.map((c) => (
                                    <option key={c.id} value={c.id}>
                                        {c.name}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div className="md:col-span-2">
                            <label className="zentra-label">Observaciones</label>
                            <textarea
                                className="zentra-textarea min-h-[80px]"
                                value={form.notes || ""}
                                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                                data-testid="edit-property-notes"
                            />
                        </div>
                        <div className="md:col-span-2">
                            <label className="zentra-label flex items-center gap-1.5">
                                Instrucciones fijas para limpieza
                                <span className="zentra-chip bg-zentra-accent/20 border-zentra-accent text-zentra-primary text-[10px]">
                                    se aplican SIEMPRE
                                </span>
                            </label>
                            <textarea
                                className="zentra-textarea min-h-[120px] font-mono-zentra text-xs"
                                value={form.fixed_instructions || ""}
                                onChange={(e) => setForm({ ...form, fixed_instructions: e.target.value })}
                                placeholder="Ej. CON ENTRADA: Cambiar lavadora, dejar 1 lavadora puesta, dejar otra colocada. Si necesita lavar dos cargas avisar. Llaves en cajetín (código 1234)..."
                                data-testid="edit-property-fixed-instructions"
                            />
                        </div>
                        <div>
                            <label className="zentra-label">Estado de llaves</label>
                            <select
                                className="zentra-select"
                                value={form.key_access || ""}
                                onChange={(e) => setForm({ ...form, key_access: e.target.value })}
                                data-testid="edit-property-key-access"
                            >
                                <option value="">— Sin especificar —</option>
                                <option value="Llaves dentro">Llaves dentro</option>
                                <option value="Llaves cajetín">Llaves cajetín</option>
                                <option value="Llaves recepción">Llaves recepción</option>
                                <option value="No hay llaves">No hay llaves</option>
                            </select>
                        </div>
                        <div>
                            <label className="zentra-label">Referencia del servicio</label>
                            <input
                                className="zentra-input"
                                value={form.service_ref || ""}
                                onChange={(e) => setForm({ ...form, service_ref: e.target.value })}
                                placeholder="224 GRAN CARDENAL (SUNSTAY)"
                                data-testid="edit-property-service-ref"
                            />
                        </div>
                        <div>
                            <label className="zentra-label">Precio limpieza (€)</label>
                            <input
                                type="number"
                                min="0"
                                step="0.01"
                                className="zentra-input"
                                value={form.cleaning_price_eur ?? 0}
                                onChange={(e) => setForm({ ...form, cleaning_price_eur: e.target.value })}
                                placeholder="35"
                                data-testid="edit-property-cleaning-price"
                            />
                            <p className="text-[11px] text-zentra-muted mt-1">
                                Importe que se factura al cliente por cada limpieza realizada.
                            </p>
                        </div>
                        {error && (
                            <div className="md:col-span-2 rounded-md border border-red-200 bg-red-50 text-red-800 px-3 py-2 text-sm">
                                {error}
                            </div>
                        )}
                    </div>
                )}

                {!editing && property.notes && (
                    <p className="mt-5 text-sm text-zentra-ink bg-zentra-bg/40 p-3 rounded-md">{property.notes}</p>
                )}
            </div>

            {/* iCal sync */}
            <ICalSourcesPanel
                propertyId={id}
                sources={property.ical_sources || []}
                onUpdated={(updated) => setData((d) => ({ ...d, property: { ...d.property, ical_sources: updated } }))}
            />

            {/* Tasks history */}
            <section className="zentra-card-static p-5" data-testid="property-tasks">
                <PropertyTasksList tasks={tasks} />
            </section>

            {/* Incidents */}
            <section className="zentra-card-static p-5" data-testid="property-incidents">
                <h3 className="font-display font-semibold text-zentra-ink mb-3">Incidencias asociadas</h3>
                {incidents.length === 0 ? (
                    <p className="text-sm text-zentra-muted py-4">Sin incidencias.</p>
                ) : (
                    <div className="space-y-2">
                        {incidents.map((inc) => (
                            <div key={inc.id} className="flex items-center justify-between p-3 border border-zentra-border rounded-md">
                                <div className="min-w-0">
                                    <p className="font-medium text-sm text-zentra-ink">{inc.title}</p>
                                    <p className="text-xs text-zentra-muted">{INCIDENT_TYPE_LABELS[inc.type] || inc.type}</p>
                                </div>
                                <div className="flex items-center gap-2">
                                    <StateBadge value={inc.priority} dict={PRIORITY_LABELS} />
                                    <StateBadge value={inc.state} dict={INCIDENT_STATE_LABELS} />
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </section>
        </div>
    );
}

function Spec({ icon: Icon, value, label }) {
    return (
        <div className="rounded-md border border-zentra-border bg-zentra-bg/30 p-3 flex items-center gap-3">
            <Icon size={18} className="text-zentra-olive" />
            <div>
                <p className="font-display font-semibold text-zentra-ink">{value}</p>
                <p className="text-[10px] uppercase tracking-widest text-zentra-muted">{label}</p>
            </div>
        </div>
    );
}

function ICalSourcesPanel({ propertyId, sources, onUpdated }) {
    const [adding, setAdding] = useState(false);
    const [syncing, setSyncing] = useState(false);
    const [error, setError] = useState("");
    const [newSrc, setNewSrc] = useState({ url: "", source: "airbnb", label: "" });
    const [syncResult, setSyncResult] = useState(null);

    const add = async (e) => {
        e?.preventDefault?.();
        if (!newSrc.url.trim()) {
            setError("Indica la URL del feed iCal");
            return;
        }
        setError("");
        try {
            const { data } = await api.post(`/properties/${propertyId}/ical-sources`, newSrc);
            onUpdated(data.ical_sources);
            setNewSrc({ url: "", source: "airbnb", label: "" });
            setAdding(false);
        } catch (err) {
            setError(formatApiError(err));
        }
    };

    const remove = async (idx) => {
        if (!window.confirm("¿Eliminar esta fuente iCal? Las reservas ya importadas se mantienen.")) return;
        const { data } = await api.delete(`/properties/${propertyId}/ical-sources/${idx}`);
        onUpdated(data.ical_sources);
    };

    const sync = async () => {
        setSyncing(true);
        setError("");
        setSyncResult(null);
        try {
            const { data } = await api.post(`/properties/${propertyId}/ical-sync`);
            onUpdated(data.ical_sources);
            setSyncResult({
                new_bookings: data.new_bookings,
                tasks_created: data.tasks_created,
                synced_sources: data.synced_sources,
            });
        } catch (err) {
            setError(formatApiError(err));
        } finally {
            setSyncing(false);
        }
    };

    return (
        <section className="zentra-card-static p-5" data-testid="ical-sources-panel">
            <div className="flex items-center justify-between mb-3">
                <div>
                    <h3 className="font-display font-semibold text-zentra-ink">Sincronización iCal</h3>
                    <p className="text-xs text-zentra-muted mt-0.5">
                        Pega aquí el feed iCal de Airbnb, Booking.com o Vrbo. Las reservas importadas crean automáticamente la tarea de limpieza del check-out.
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    {sources.length > 0 && (
                        <button
                            type="button"
                            className="zentra-btn-ghost"
                            onClick={sync}
                            disabled={syncing}
                            data-testid="sync-ical-button"
                        >
                            <RefreshCw size={14} className={syncing ? "animate-spin" : ""} />
                            {syncing ? "Sincronizando…" : "Sincronizar ahora"}
                        </button>
                    )}
                    {!adding && (
                        <button
                            type="button"
                            className="zentra-btn-primary"
                            onClick={() => setAdding(true)}
                            data-testid="add-ical-button"
                        >
                            <Plus size={14} /> Añadir feed
                        </button>
                    )}
                </div>
            </div>

            {syncResult && (
                <div className="mb-3 rounded-md border border-emerald-200 bg-emerald-50 text-emerald-900 px-3 py-2 text-sm">
                    <CheckCircle2 size={14} className="inline mr-1.5 -mt-0.5" />
                    Sincronizado · {syncResult.synced_sources} fuente(s) · {syncResult.new_bookings} reserva(s) nueva(s) · {syncResult.tasks_created} tarea(s) auto-creadas
                </div>
            )}

            {adding && (
                <form onSubmit={add} className="grid md:grid-cols-3 gap-3 mb-3 p-3 rounded-md border border-zentra-border bg-zentra-bg/40">
                    <div className="md:col-span-3">
                        <label className="zentra-label">URL iCal</label>
                        <input
                            className="zentra-input font-mono-zentra text-xs"
                            value={newSrc.url}
                            onChange={(e) => setNewSrc({ ...newSrc, url: e.target.value })}
                            placeholder="https://www.airbnb.com/calendar/ical/12345.ics?s=ABCD..."
                            data-testid="ical-url-input"
                            required
                        />
                    </div>
                    <div>
                        <label className="zentra-label">Origen</label>
                        <select
                            className="zentra-select"
                            value={newSrc.source}
                            onChange={(e) => setNewSrc({ ...newSrc, source: e.target.value })}
                            data-testid="ical-source-select"
                        >
                            <option value="airbnb">Airbnb</option>
                            <option value="booking">Booking.com</option>
                            <option value="vrbo">Vrbo</option>
                            <option value="avaibook">Avaibook</option>
                            <option value="avantio">Avantio</option>
                            <option value="other">Otro</option>
                        </select>
                    </div>
                    <div>
                        <label className="zentra-label">Etiqueta (opcional)</label>
                        <input
                            className="zentra-input"
                            value={newSrc.label}
                            onChange={(e) => setNewSrc({ ...newSrc, label: e.target.value })}
                            placeholder="Airbnb principal"
                            data-testid="ical-label-input"
                        />
                    </div>
                    <div className="flex items-end gap-2">
                        <button type="submit" className="zentra-btn-primary w-full justify-center" data-testid="save-ical-source">
                            Añadir
                        </button>
                        <button
                            type="button"
                            className="zentra-btn-ghost"
                            onClick={() => {
                                setAdding(false);
                                setError("");
                            }}
                        >
                            Cancelar
                        </button>
                    </div>
                    {error && (
                        <div className="md:col-span-3 rounded-md border border-red-200 bg-red-50 text-red-800 px-3 py-2 text-sm">
                            {error}
                        </div>
                    )}
                </form>
            )}

            {!adding && error && (
                <div className="mb-3 rounded-md border border-red-200 bg-red-50 text-red-800 px-3 py-2 text-sm">{error}</div>
            )}

            {sources.length === 0 ? (
                <p className="text-sm text-zentra-muted py-2">
                    No hay fuentes iCal configuradas. En Airbnb: Calendario → Disponibilidad → Sincronizar calendarios → Exportar.
                </p>
            ) : (
                <ul className="space-y-2">
                    {sources.map((s, idx) => (
                        <li
                            key={idx}
                            className="flex items-start gap-3 p-3 rounded-md border border-zentra-border bg-white"
                            data-testid={`ical-source-${idx}`}
                        >
                            <Link2 size={16} className="text-zentra-olive mt-0.5" />
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                    <p className="font-semibold text-sm text-zentra-ink capitalize">
                                        {s.label || s.source}
                                    </p>
                                    <span className="zentra-chip bg-zentra-bg text-zentra-muted border-zentra-border">
                                        {s.source}
                                    </span>
                                    {s.last_status === "ok" && (
                                        <span
                                            className="zentra-chip bg-emerald-100 text-emerald-900 border-emerald-200"
                                            data-testid={`ical-source-status-${idx}`}
                                        >
                                            <CheckCircle2 size={10} className="mr-1" /> ok
                                        </span>
                                    )}
                                    {s.last_status === "error" && (
                                        <span
                                            className="zentra-chip bg-red-100 text-red-800 border-red-200"
                                            data-testid={`ical-source-status-${idx}`}
                                        >
                                            <AlertCircle size={10} className="mr-1" /> error
                                        </span>
                                    )}
                                </div>
                                <p className="text-xs text-zentra-muted font-mono-zentra truncate mt-0.5">{s.url}</p>
                                {s.last_synced && (
                                    <p className="text-[11px] text-zentra-muted mt-1">
                                        Última sync: {new Date(s.last_synced).toLocaleString("es-ES")}
                                    </p>
                                )}
                                {s.last_error && (
                                    <p className="text-[11px] text-red-700 mt-1">{s.last_error}</p>
                                )}
                            </div>
                            <button
                                onClick={() => remove(idx)}
                                className="text-zentra-muted hover:text-red-600"
                                data-testid={`delete-ical-${idx}`}
                                aria-label="Eliminar fuente"
                            >
                                <Trash2 size={14} />
                            </button>
                        </li>
                    ))}
                </ul>
            )}
        </section>
    );
}


/**
 * Property tasks list with date filter (last 30d default + "all").
 * Adds a "Archivos" column showing the media count and links to the cleaning
 * detail where the office can upload more files.
 */
function PropertyTasksList({ tasks }) {
    const [range, setRange] = useState("30d");

    const todayIso = new Date().toISOString().slice(0, 10);
    const cutoffDate = (days) => {
        const d = new Date();
        d.setDate(d.getDate() - days);
        return d.toISOString().slice(0, 10);
    };
    let filtered = tasks;
    if (range === "30d") filtered = tasks.filter((t) => (t.scheduled_date || "") >= cutoffDate(30));
    else if (range === "90d") filtered = tasks.filter((t) => (t.scheduled_date || "") >= cutoffDate(90));

    filtered = [...filtered].sort((a, b) => (b.scheduled_date || "").localeCompare(a.scheduled_date || ""));

    return (
        <>
            <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
                <div>
                    <h3 className="font-display font-semibold text-zentra-ink">Historial de servicios</h3>
                    <p className="text-xs text-zentra-muted">
                        {filtered.length} {filtered.length === 1 ? "limpieza" : "limpiezas"} ·{" "}
                        {range === "30d" ? "últimos 30 días" : range === "90d" ? "últimos 90 días" : "histórico completo"}
                    </p>
                </div>
                <div className="inline-flex items-center gap-1 text-xs">
                    {[
                        { v: "30d", l: "30 días" },
                        { v: "90d", l: "90 días" },
                        { v: "all", l: "Todo" },
                    ].map((opt) => (
                        <button
                            key={opt.v}
                            type="button"
                            onClick={() => setRange(opt.v)}
                            className={`px-2.5 py-1 rounded-md border font-semibold transition ${
                                range === opt.v
                                    ? "border-zentra-primary bg-zentra-primary text-white"
                                    : "border-zentra-border bg-white text-zentra-ink hover:border-zentra-primary"
                            }`}
                            data-testid={`property-tasks-range-${opt.v}`}
                        >
                            {opt.l}
                        </button>
                    ))}
                </div>
            </div>
            {filtered.length === 0 ? (
                <p className="text-sm text-zentra-muted py-4">Sin tareas en este rango.</p>
            ) : (
                <table className="zentra-table">
                    <thead>
                        <tr>
                            <th>Fecha</th>
                            <th>Tipo</th>
                            <th>Estado</th>
                            <th>Archivos</th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        {filtered.map((t) => {
                            const count = (t.media || []).length;
                            return (
                                <tr key={t.id}>
                                    <td className="font-mono-zentra text-xs">
                                        <span className={t.scheduled_date === todayIso ? "text-zentra-accent-strong font-bold" : ""}>
                                            {t.scheduled_date}
                                        </span>
                                    </td>
                                    <td>{TASK_TYPE_LABELS[t.type] || t.type}</td>
                                    <td>
                                        <StateBadge value={t.state} dict={TASK_STATE_LABELS} />
                                    </td>
                                    <td>
                                        {count > 0 ? (
                                            <span className="inline-flex items-center gap-1 text-xs font-semibold text-zentra-accent-strong">
                                                <Images size={12} /> {count}
                                            </span>
                                        ) : (
                                            <span className="text-[11px] text-zentra-muted italic">—</span>
                                        )}
                                    </td>
                                    <td className="text-right">
                                        <Link
                                            to={`/limpiezas/${t.id}`}
                                            className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-1 rounded-md border border-zentra-border bg-white hover:border-zentra-primary"
                                            data-testid={`property-task-open-${t.id}`}
                                        >
                                            <Plus size={11} /> Archivos / Detalle
                                        </Link>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            )}
        </>
    );
}
